import isEqual from "lodash/isEqual";
import * as React from "react";
import * as toast from "@app/viewable-fields/toast/ViewableFieldsToast"
import { Dictionary, difference, groupBy, uniqBy } from "lodash";
import { TableType, tableTypeRestrictions, ViewableField } from "@app/viewable-fields/viewableFields";
import { FlyoutContextOutgoingProps, withFlyoutContext } from "@app/shared/Flyouts/FlyoutContext";
import { ViewableFieldsContextProps, withViewableFields } from "@app/viewable-fields/ViewableFieldsContext";
import { Accordion } from "@app/shared/Accordion";
import CheckItem from "@app/shared/CheckItem";
import Project from "@app/projects/project";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import RoleRestricted from "@app/user/RoleRestricted";

/** Represents the possible check states for a provider field. */
enum CheckStatus {
  Checked = "checked",
  Unchecked = "unchecked",
  Indeterminate = "indeterminate",
}

type ViewableProviderFieldsProps = ViewableFieldsContextProps & FlyoutContextOutgoingProps & {
  defaultOpen: boolean;
  onChange: (displayOptionsDirty: boolean) => void;
  project: Project;
  tableType: TableType;
};

interface ViewableProviderFieldsState {
  draggedItem: ViewableField;
  error: Error;
  project: Project;
  tableType: TableType;
  tempViewableFields: ViewableField[];
}

class ViewableProviderFields extends React.Component<ViewableProviderFieldsProps, ViewableProviderFieldsState> {
  private readonly submitButtonRef = React.createRef<HTMLButtonElement>();
  private initialViewableFields: ViewableField[] = [];
  private draggedFieldId: number;
  private draggedOverFieldId: number;

  constructor(props: ViewableProviderFieldsProps) {
    super(props);

    this.state = {
      draggedItem: null,
      error: null,
      project: this.props.project,
      tableType: this.props.tableType,
      tempViewableFields: [],
    };
  }

  public componentDidMount() {
    try {
      this.gatherViewableFields();
    } catch (error) {
      this.setState({ error });
    }
  }

  public componentDidUpdate(prevProps: ViewableProviderFieldsProps) {
    if (!isEqual(prevProps.viewableFieldsByTableType.get(prevProps.tableType), this.props.viewableFieldsByTableType.get(this.props.tableType))) {
      try {
        this.gatherViewableFields();
      } catch (error) {
        this.setState({ error });
      }
      this.props.onChange(this.isDirty());
    }
  }

  public componentWillUnmount() {
    this.props.onChange(false);
  }

  public render() {
    return (this.state.tempViewableFields?.length > 0) && (
      this.renderForm()
    );
  }

  private renderForm() {
    const isShared = this.state.project && this.state.project.resource.isShared;
    const haveAccess = (!isShared ? true : false);
    const tableTypeTitle = this.props.tableType.replace(/([A-Z])/g, " $1").trim();
    const title = "Viewable Provider Fields" + (this.props.tableType ? " for " + tableTypeTitle : "");
    const [cargoFields, allFields] = this.separateCargoFields(this.state.tempViewableFields);
    const groups = Object.keys(groupBy(allFields, "group"));

    return (
      <RoleRestricted allowedRoles={tableTypeRestrictions.get(this.props.tableType)}>
        <Accordion title={title} defaultOpen={this.props.defaultOpen} className="viewable-fields" testId={`viewable-provider-fields__${title}-accordion`}>
          <div className="mb15">
            Edit the {tableTypeTitle} fields to include...
          </div>
          {this.renderFormRowHeader(haveAccess)}
          {groups.map((group, i) => {
            return (
              <div key={i}>
                {allFields.filter((x) => x.group === group).length > 0 && group !== "" &&
                  <div className="row pb10 pt10 grid-row">
                    <b>{group}</b>
                  </div>
                }
                <div className="grid-striped">
                  {allFields.filter((x) => x.group === group).map((row) => this.renderFormRow(row, isShared, haveAccess))}
                </div>

              </div>
            );
          })}
          {cargoFields.length > 0 &&
            <div>
              <div className="row pb10 pt10 grid-row">
                <b>Cargo</b>
              </div>
              <div className="grid-striped"
              >
                {cargoFields.map((row) => this.renderCargoRow(row, isShared, haveAccess))}
              </div>
            </div>
          }
          {this.renderFormFooter(haveAccess)}
        </Accordion>
      </RoleRestricted>
    );
  }

  private renderFormRowHeader(haveAccess: boolean) {
    return (
      <div className="form-group row">
        <div className={haveAccess ? "col-1" : "d-none"}><label className="col-form-label">&nbsp;</label></div>
        <div className={haveAccess ? "col-5" : "d-none"}><label className="col-form-label">Column</label></div>
        <div className={haveAccess ? "col-5" : "col-10"}><label className="col-form-label">Display Name</label></div>
        <div className="col-1 text-center"><label className="col-form-label">Visible</label></div>
      </div>
    );
  }

  private renderFormFooter(haveAccess: boolean) {
    return (
      <>
        <div className="form-group row">
          <div className={haveAccess ? "col-2 mt10" : "d-none"} />
          <div className={haveAccess ? "col-10 mt10" : "col-12"}>
            <QESButton qesTestId="viewable-provider-fields_submit" qesType="primary" qesRef={this.submitButtonRef} className="m0 pull-right" onClick={this.handleProviderFormSubmit} disabled={this.isFormValid()}>Submit</QESButton>
            <QESButton qesTestId="viewable-provider-fields_reset-project" qesType="secondary-danger" className="pull-right ml5" onClick={this.handleReset}>Reset</QESButton>
            <div className="clearfix" />
          </div>
        </div>
      </>
    );
  }

  private renderFormRow(rowData: ViewableField, isShared: boolean, haveAccess: boolean) {
    const index = this.state.tempViewableFields.findIndex((tvf) => tvf.backingFieldName === rowData.backingFieldName);
    return (
      <div
        className="row pb10 pt10 grid-row"
        key={index}
      >
        {this.getRowBody(rowData, isShared, haveAccess, index)}
      </div>
    );
  }

  private renderCargoRow(rowData: ViewableField, isShared: boolean, haveAccess: boolean) {
    const index = this.state.tempViewableFields.findIndex((tvf) => tvf.backingFieldName === rowData.backingFieldName);
    return (
      <div
        className="row pb10 pt10 grid-row"
        key={index}
        onDragStart={(e) => this.handleDragStart(e, index)}
        onDragEnter={(e) => this.handleDragEnter(e, index)}
        onDragOver={(e) => this.handleDragEnter(e, index)}
        onDragEnd={this.handleDragEnd}
        draggable
      >
        {this.getRowBody(rowData, isShared, haveAccess, index)}
      </div>
    );

  }

  private getRowBody(rowData: ViewableField, isShared: boolean, haveAccess: boolean, index: number) {
    const checkStatus: CheckStatus = rowData.visible && !rowData.fieldExclude
      ? CheckStatus.Checked
      : CheckStatus.Unchecked;

    const displayNameOk = this.isFormValid(index);

    return (
      <>
        <div className={haveAccess ? "col-1 pull-left" : "d-none"} >
          {rowData.isCargoColumn && < div className="drag-indicator h-100" />}
        </div>
        <div className={haveAccess ? "col-5" : "d-none"}>
          <QESSelect label="Field Name" hideLabel={true} testId={`viewable-field-name--${rowData.backingFieldName}`} className="form-control" name="name" onChange={this.handleSelectChange(index)} value={rowData.backingFieldName} disabled={rowData.alwaysVisible}>
            {this.state.tempViewableFields.map((option, i) => (
              ((option.backingFieldName === rowData.backingFieldName || option.fieldExclude === true) && option.group === rowData.group ? <option key={i} value={option.backingFieldName}>{option.defaultDisplayName}</option> : null)
            ))}
          </QESSelect>
        </div>
        <div className={haveAccess ? `col-5 ${displayNameOk ? "" : "has-error"}` : "col-10"}>
          <input
            className={!haveAccess ? "form-control disabled-region" : "form-control"}
            name="displayName"
            maxLength={255}
            onChange={this.handleInputChange(index)}
            type="text"
            value={rowData.displayName}
            disabled={!haveAccess || rowData.disableDisplayNameEdit}
          />
        </div>
        <div className="col-1 text-center">
          <CheckItem
            id={rowData.displayName}
            name={""}
            checked={checkStatus === CheckStatus.Checked}
            disabled={rowData.alwaysVisible}
            indeterminate={false}
            onChecked={() => this.handleFieldVisibleCheck(index, true, isShared)}
            onUnchecked={() => this.handleFieldVisibleCheck(index, false, isShared)}
          />
        </div>
      </>
    )
  }

  private isDirty = (viewableFields: ViewableField[] = this.state.tempViewableFields) => {
    if (this.initialViewableFields.length === 0) {
      return false;
    }
    const returnValue = !isEqual(viewableFields, this.initialViewableFields);
    return returnValue;
  }

  private handleFieldVisibleCheck = (idx: number, checked: boolean, shared: boolean) => {
    const viewableFields: ViewableField[] = [...this.state.tempViewableFields];

    viewableFields[idx] = {
      ...viewableFields[idx],
      defaultVisible: shared ? viewableFields[idx].defaultVisible : checked,
      fieldExclude: !checked,
      visible: checked,
    };

    this.props.onChange(this.isDirty(viewableFields));
    this.setState({ tempViewableFields: viewableFields });
  }

  private handleInputChange = (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const desc = e.target.value;
    const viewableFields: ViewableField[] = [...this.state.tempViewableFields];

    viewableFields[idx] = {
      ...viewableFields[idx],
      displayName: desc,
    };

    this.props.onChange(this.isDirty(viewableFields));
    this.setState({ tempViewableFields: viewableFields });
  }

  private handleSelectChange = (idx: number) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nameValue = e.target.value;
    const found = this.state.tempViewableFields.some((row) => row.backingFieldName === nameValue && row.fieldExclude === false);
    if (!found) {
      const viewableFields: ViewableField[] = [...this.state.tempViewableFields];
      const newRow = viewableFields.find((row) => row.backingFieldName === nameValue);

      // move it to the proper spot
      viewableFields.splice(viewableFields.indexOf(newRow), 1);
      viewableFields.splice(idx, 0, newRow);
      this.props.onChange(this.isDirty(viewableFields));
      this.setState({ tempViewableFields: this.moveViewableField(viewableFields[idx].displayOrder, idx) });
    }
  }

  private isFormValid = (index?: number) => {
    const field: ViewableField = this.state.tempViewableFields[index];
    const groupedViewableFields: Dictionary<ViewableField[]> = groupBy(this.state.tempViewableFields, "group");

    let isRowDisplayNameBlank = false;
    let isRowDisplayNameDuplicate = false;

    if (field) {
      // assessing the validity of a specific row
      isRowDisplayNameBlank = field.displayName ? false : true;
      isRowDisplayNameDuplicate = groupedViewableFields[field.group].filter((x) => (x.displayName === field.displayName) && field.displayName).length > 1;

      return !isRowDisplayNameBlank && !isRowDisplayNameDuplicate;
    } else {
      // assessing the validity of the entire form
      for (const [, value] of Object.entries(groupedViewableFields)) {
        isRowDisplayNameDuplicate = difference(value, uniqBy(value, "displayName")).length > 0;
        isRowDisplayNameBlank = value.filter((x) => !x.displayName).length > 0;

        if (isRowDisplayNameDuplicate || isRowDisplayNameBlank) {
          break;
        }
      }

      return isRowDisplayNameBlank || isRowDisplayNameDuplicate;
    }
  }

  private handleReset = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    await this.props.resetByTableType(this.state.tableType);
    toast.toastInfo("Project " + this.state.project.resource.name + " has been reset.");
    this.props.onChange(false);
    this.closeViewableProviderFields();
  }

  private handleProviderFormSubmit = () => {
    this.props.updateByTableType(this.state.tableType, this.state.tempViewableFields).then(() => {
      this.initialViewableFields = { ...this.state.tempViewableFields };
      this.props.onChange(false);
      toast.toastInfo("Viewable Provider Fields updated.");
      this.closeViewableProviderFields();
    }, () => {
      toast.toastError(<>Viewable Providers Fields were not updated. If this continues, contact your consultant at <a href="mailto:consulting@questanalytics.com">consulting@questanalytics.com.</a></>);
    });
  }

  private closeViewableProviderFields = () => {
    // the props.onChange state update in Options.tsx wont have taken effect yet,
    // so using setIsFlyoutVisible instead of handleCloseFlyout bypasses the "are you sure" modal
    this.props.setIsFlyoutVisible(false);
  }

  private gatherViewableFields = () => {
    let viewableFields: ViewableField[] = this.props.viewableFieldsByTableType.get(this.props.tableType) || [];

    //set initial displayOrder for all viewable fields    
    if (!viewableFields[0].displayOrder) {
      viewableFields = viewableFields.map((value, index) => {
        value.displayOrder = index;
        return value;
      });
    }

    this.initialViewableFields = viewableFields;
    this.setState({
      tempViewableFields: viewableFields,
    });
  }

  private separateCargoFields = (viewableFields: ViewableField[]): ViewableField[][] => {
    return viewableFields.reduce(([v, c], elem) => (elem.isCargoColumn ? [[...v, elem], c] : [v, [...c, elem]]), [[], []]);
  }

  // for drag and drop, we can't rely on state to toggle these classes because updating state and rerendering the component triggers dragEnd
  private handleDragStart = (e: React.DragEvent<HTMLSpanElement>, index: number) => {
    this.draggedFieldId = index;
  };

  private handleDragEnd = () => {
    this.setState({
      tempViewableFields: this.moveViewableField(this.draggedFieldId, this.draggedOverFieldId),
    });
  };

  private handleDragEnter = (e: React.DragEvent<HTMLSpanElement>, index: number) => {
    e.preventDefault();
    this.draggedOverFieldId = index;
  };

  private moveViewableField = (fieldIndex: number, destIndex: number): ViewableField[] => {
    const viewableFields = [...this.state.tempViewableFields];

    const field = this.state.tempViewableFields[fieldIndex];

    let index;
    if (fieldIndex > destIndex) {
      for (index = fieldIndex; index > destIndex; index--) {
        viewableFields[index] = this.state.tempViewableFields[index - 1];
        viewableFields[index].displayOrder = index;
      }
    }
    else {
      for (index = fieldIndex + 1; index <= destIndex; index++) {
        viewableFields[index - 1] = this.state.tempViewableFields[index];
        viewableFields[index - 1].displayOrder = index - 1;
      }
    }

    viewableFields[destIndex] = field;
    viewableFields[destIndex].displayOrder = destIndex;

    return viewableFields;
  };
}

const WrappedViewableProviderFields = withViewableFields(withFlyoutContext(ViewableProviderFields));

export { WrappedViewableProviderFields as ViewableProviderFields };
